import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button, SegmentedTabs } from "@akuna/ui";
import { SearchField } from "@akuna/ui";
import { Separator } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@akuna/ui";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as DateRangeCalendar } from "@/components/ui/calendar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ShoppingCart, X, List, CalendarCheck, ChevronLeft, ChevronRight, Filter, CalendarIcon, Plus } from "lucide-react";
import { useSales } from '@/hooks/useSales';
import { usePetsByOwner } from '@/hooks/usePets';
import { summarizeCartPets, type SalePet } from '@/lib/sales/cart-pets';
import { useSalesList, type SaleListItem } from '@/hooks/useSalesList';
import { useFinalizedAppointments } from '@/hooks/useFinalizedAppointments';
import { useCurrentClinicId } from '@/hooks/useAppointments';
import { useCommissionItemProfessionals, useCommissionProfessionals } from '@/hooks/useCommissions';
import { useCurrentClinicCustomerId } from '@/hooks/useCurrentClinicCustomerId';
import { useItems } from '@/hooks/useItems';
import { useSalesFiscalSummary } from '@/hooks/useFiscalDocuments';
import { usePatientPackages, type PatientPackageSummary } from '@/hooks/usePatientPackages';
import type { PrefillFromAppointment } from '@/components/erp/pos/PatientAndCpfSection';
import { ProductSearch, type PdvItem } from '@/components/erp/pos/ProductSearch';
import { AdHocItemDialog } from '@/components/erp/pos/AdHocItemDialog';
import { isProductSaleItem } from '@/lib/sales/itemClassification';
import { CartItems } from '@/components/erp/pos/CartItems';
import { PaymentSection } from '@/components/erp/pos/PaymentSection';
import { PatientAndCpfSection } from '@/components/erp/pos/PatientAndCpfSection';
import { ExistingPatientPackageSelector } from '@/components/erp/pos/ExistingPatientPackageSelector';
import { Receipt } from '@/components/erp/pos/Receipt';
import { SaleDetailModal } from '@/components/erp/pos/SaleDetailModal';
import { PermissionGuard } from "@/components/PermissionGuard";
import { AppointmentStatusBadge, AppointmentTypePill } from "@/components/patients/appointment-ui-badges";
import { APPOINTMENT_STATUS_LABEL } from "@/components/patients/appointment-ui-meta";
import { endOfDay, format, isBefore, startOfDay, subDays } from "date-fns";
import { enUS, ptBR } from "date-fns/locale";
import type { DateRange } from "react-day-picker";
import { formatCpf } from "@/lib/utils/cpf-mask";
import {
  buildSuggestedPackageChargeFromCatalogItem,
  buildSuggestedPackageChargeFromPatientPackage,
  type SuggestedPackageCharge,
} from '@/lib/utils/suggested-package-charge';
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useResumeSaleFromLocation } from "@/hooks/useResumeSaleFromLocation";
import type { ResumeSaleContext, ResumeSaleData, ResumeSaleFailure } from "@/lib/sales/resumeSale";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { useMobileHeader } from "@/contexts/MobileHeaderContext";
import { useFiscalEnabled } from "@/hooks/useFiscalEnabled";
import { getGroomingStatusLabel } from "@/lib/utils/grooming-status";

type SalesAppointmentItemPrefill = {
  item_id: string;
  qty: number;
  title_snapshot?: string | null;
  item?: { name?: string; sale_price?: number | null; type?: "PRODUTO" | "SERVICO" | "PACOTE" | null } | null;
};

type SalesPrefillLocationState = {
  prefillAppointmentForSale?: PrefillFromAppointment & {
    appointmentItems?: SalesAppointmentItemPrefill[];
    suggestedPackageCharges?: SuggestedPackageCharge[];
    vet_user_id?: string | null;
    vet?: { full_name?: string | null } | null;
  };
  openSaleId?: string;
};

type SalesTab = "new" | "appointments" | "history";

const SALES_TAB_BY_ROUTE: Record<string, SalesTab> = {
  new: "new",
  appointments: "appointments",
  history: "history",
  nova: "new",
  agendamentos: "appointments",
  lista: "history",
};

const DEFAULT_SALE_STATUSES = ["A_RECEBER", "PAGA", "CANCELADA", "DEVOLVIDA"];
const SALES_PAGE_SIZE = 50;
const EMPTY_COMMISSION_MAP = new Map<string, Set<string>>();

const defaultSalesStartDate = () => format(subDays(new Date(), 6), "yyyy-MM-dd");
const defaultSalesEndDate = () => format(new Date(), "yyyy-MM-dd");

export default function Sales() {
  const { t, i18n } = useTranslation("erp");
  useMobileHeader({ title: t("salesPage.title") }, [t]);
  const dateLocale = i18n.language === "pt" ? ptBR : enUS;
  const fiscalEnabled = useFiscalEnabled();
  const navigate = useNavigate();
  const location = useLocation();
  const { salesTab } = useParams<{ salesTab?: string }>();
  const [showReceipt, setShowReceipt] = useState(false);
  const [lastSaleId, setLastSaleId] = useState('');
  const [detailSaleId, setDetailSaleId] = useState<string | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [resumeContext, setResumeContext] = useState<{
    name: string;
    kind: ResumeSaleContext;
    returnTo: string | null;
  } | null>(null);
  const routeTab = useMemo<SalesTab | null>(() => {
    if (!salesTab) return null;
    return SALES_TAB_BY_ROUTE[salesTab] ?? null;
  }, [salesTab]);
  const activeTab = routeTab ?? "new";
  const [pendingAppointment, setPendingAppointment] = useState<PrefillFromAppointment | null>(null);
  const [receivableDialogOpen, setReceivableDialogOpen] = useState(false);
  const [salesPage, setSalesPage] = useState(1);
  const [finalizedPage, setFinalizedPage] = useState(1);

  useEffect(() => {
    if (!routeTab) {
      navigate("/sales/new", { replace: true });
    }
  }, [navigate, routeTab]);

  // Histórico filters (UI pattern similar to Calendar.tsx)
  const [salesSearchTerm, setSalesSearchTerm] = useState("");
  const [selectedSaleStatuses, setSelectedSaleStatuses] = useState<string[]>(DEFAULT_SALE_STATUSES);
  const [salesStartDate, setSalesStartDate] = useState(defaultSalesStartDate);
  const [salesEndDate, setSalesEndDate] = useState(defaultSalesEndDate);
  const [salesPeriodOpen, setSalesPeriodOpen] = useState(false);
  const [salesPeriodSelectionStep, setSalesPeriodSelectionStep] = useState<"from" | "to">("from");
  const [salesPeriodRange, setSalesPeriodRange] = useState<DateRange | undefined>(() => ({
    from: new Date(`${defaultSalesStartDate()}T00:00:00`),
    to: new Date(`${defaultSalesEndDate()}T00:00:00`),
  }));

  const { data: salesListResult, isLoading: isLoadingSales } = useSalesList({
    page: salesPage,
    pageSize: SALES_PAGE_SIZE,
    statuses: selectedSaleStatuses,
    search: salesSearchTerm,
    startDate: salesStartDate || undefined,
    endDate: salesEndDate || undefined,
  });
  const salesList = useMemo(() => salesListResult?.data ?? [], [salesListResult?.data]);
  const saleIds = useMemo(() => salesList.map((sale) => sale.id), [salesList]);
  const { data: fiscalSummaryBySale = {} } = useSalesFiscalSummary(saleIds);
  const salesTotalCount = salesListResult?.totalCount ?? 0;
  const salesTotalPages = salesListResult?.totalPages ?? 0;
  const { data: finalizedData, isLoading: isLoadingFinalized } = useFinalizedAppointments({ limit: 50 });
  const { data: clinicId } = useCurrentClinicId();
  const { data: customerId } = useCurrentClinicCustomerId();
  const { data: professionals = [] } = useCommissionProfessionals();
  const { data: commissionByItemData, isError: commissionMapFailed } = useCommissionItemProfessionals();
  // erro ao buscar quem ganha: volta a mostrar todos os profissionais, como antes; carregando: nenhum select ainda
  const commissionByItem = commissionMapFailed ? null : (commissionByItemData ?? EMPTY_COMMISSION_MAP);
  const { toast } = useToast();
  const { appointments: finalizedAppointments = [] } = finalizedData ?? {};
  const finalizedPageSize = 10;
  const finalizedTotalCount = finalizedAppointments.length;
  const finalizedTotalPages = Math.ceil(finalizedTotalCount / finalizedPageSize);
  const paginatedFinalizedAppointments = useMemo(() => {
    const start = (finalizedPage - 1) * finalizedPageSize;
    const end = start + finalizedPageSize;
    return finalizedAppointments.slice(start, end);
  }, [finalizedAppointments, finalizedPage]);

  const getPageItems = (current: number, total: number) => {
    if (total <= 1) return [1];
    const pages = new Set<number>([1, total]);
    for (let page = current - 1; page <= current + 1; page += 1) {
      if (page >= 1 && page <= total) pages.add(page);
    }
    const sortedPages = Array.from(pages).sort((a, b) => a - b);
    const items: Array<number | "ellipsis"> = [];
    let lastPage = 0;
    for (const page of sortedPages) {
      if (lastPage && page - lastPage > 1) items.push("ellipsis");
      items.push(page);
      lastPage = page;
    }
    return items;
  };

  const goToPage = (page: number) => {
    setSalesPage(page);
  };

  const goToPreviousPage = () => {
    if (salesPage > 1) {
      setSalesPage(salesPage - 1);
    }
  };

  const goToNextPage = () => {
    if (salesPage < salesTotalPages) {
      setSalesPage(salesPage + 1);
    }
  };

  const goToFinalizedPage = (page: number) => {
    setFinalizedPage(page);
  };

  const goToPreviousFinalizedPage = () => {
    if (finalizedPage > 1) {
      setFinalizedPage(finalizedPage - 1);
    }
  };

  const goToNextFinalizedPage = () => {
    if (finalizedPage < finalizedTotalPages) {
      setFinalizedPage(finalizedPage + 1);
    }
  };

  useEffect(() => {
    setSalesPage(1);
  }, [salesSearchTerm, selectedSaleStatuses, salesStartDate, salesEndDate]);

  useEffect(() => {
    setSalesPeriodRange({
      from: salesStartDate ? new Date(`${salesStartDate}T00:00:00`) : undefined,
      to: salesEndDate ? new Date(`${salesEndDate}T00:00:00`) : undefined,
    });
  }, [salesEndDate, salesStartDate]);

  useEffect(() => {
    if (salesPeriodOpen) setSalesPeriodSelectionStep("from");
  }, [salesPeriodOpen]);

  useEffect(() => {
    if (finalizedTotalPages === 0 && finalizedPage !== 1) {
      setFinalizedPage(1);
      return;
    }
    if (finalizedPage > finalizedTotalPages && finalizedTotalPages > 0) {
      setFinalizedPage(finalizedTotalPages);
    }
  }, [finalizedPage, finalizedTotalPages]);

  const saleStatusOptions = useMemo(
    () => [
      { value: "A_RECEBER", label: "A receber" },
      { value: "PAGA", label: t("salesPage.statuses.paid") },
      { value: "CANCELADA", label: t("salesPage.statuses.cancelled") },
      { value: "DEVOLVIDA", label: t("salesPage.statuses.returned") },
    ],
    [t],
  );

  const toggleSaleStatus = (value: string) => {
    setSelectedSaleStatuses((prev) => {
      const next = prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value];
      if (next.length === 0) return saleStatusOptions.map((s) => s.value);
      return next;
    });
  };

  const clearSalesFilters = () => {
    setSalesSearchTerm("");
    setSelectedSaleStatuses(DEFAULT_SALE_STATUSES);
    setSalesStartDate(defaultSalesStartDate());
    setSalesEndDate(defaultSalesEndDate());
  };

  const salesPeriodLabel = useMemo(() => {
    if (!salesStartDate || !salesEndDate) return t("salesPage.period.placeholder");
    const from = new Date(`${salesStartDate}T00:00:00`);
    const to = new Date(`${salesEndDate}T00:00:00`);
    return `${format(from, "dd/MM/yyyy", { locale: dateLocale })} - ${format(to, "dd/MM/yyyy", { locale: dateLocale })}`;
  }, [dateLocale, salesEndDate, salesStartDate, t]);

  const handleSalesPeriodDayClick = (day: Date) => {
    const clicked = startOfDay(day);

    if (salesPeriodSelectionStep === "from") {
      setSalesPeriodRange({ from: clicked, to: undefined });
      setSalesStartDate(format(clicked, "yyyy-MM-dd"));
      setSalesEndDate(format(endOfDay(clicked), "yyyy-MM-dd"));
      setSalesPeriodSelectionStep("to");
      return;
    }

    const from = salesPeriodRange?.from ? startOfDay(salesPeriodRange.from) : clicked;
    const nextFrom = isBefore(clicked, from) ? clicked : from;
    const nextTo = isBefore(clicked, from) ? from : clicked;
    setSalesPeriodRange({ from: nextFrom, to: nextTo });
    setSalesStartDate(format(nextFrom, "yyyy-MM-dd"));
    setSalesEndDate(format(nextTo, "yyyy-MM-dd"));
    setSalesPeriodSelectionStep("from");
    setSalesPeriodOpen(false);
  };

  const activeSalesFiltersCount = useMemo(() => {
    const hasDefaultStatuses =
      selectedSaleStatuses.length === DEFAULT_SALE_STATUSES.length &&
      DEFAULT_SALE_STATUSES.every((status) => selectedSaleStatuses.includes(status));
    const hasDefaultPeriod =
      salesStartDate === defaultSalesStartDate() &&
      salesEndDate === defaultSalesEndDate();
    return (salesSearchTerm.trim() ? 1 : 0) + (hasDefaultStatuses ? 0 : 1) + (hasDefaultPeriod ? 0 : 1);
  }, [salesEndDate, salesSearchTerm, salesStartDate, selectedSaleStatuses]);

  const saleHasProducts = useCallback((sale: SaleListItem) => {
    return (sale.sale_items ?? []).some(isProductSaleItem);
  }, []);

  const saleReceivableAmount = (sale: SaleListItem) => {
    const rows = Array.isArray(sale.sale_payments) ? sale.sale_payments : [];
    return rows
      .filter((p) => p.method === "A_RECEBER" && !p.received_at)
      .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  };

  const saleStatusLabel = (sale: SaleListItem) => {
    const receivable = saleReceivableAmount(sale);
    if (sale.status === "CANCELADA") {
      return {
        label: t("salesPage.statuses.cancelled"),
        amount: null,
        className:
          "bg-destructive/10 text-destructive border-destructive/20 dark:bg-destructive/20 dark:text-destructive",
      };
    }
    if (sale.status === "DEVOLVIDA") {
      return {
        label: t("salesPage.statuses.returned"),
        amount: null,
        className: "bg-muted text-muted-foreground border-border",
      };
    }
    if (receivable > 0) {
      return {
        label: t("salesPage.statuses.receivable"),
        amount: receivable,
        className:
          "bg-amber-100 text-amber-900 border-amber-200 dark:bg-amber-900/30 dark:text-amber-100 dark:border-amber-900/40",
      };
    }
    // cortesia: venda paga com 100% de desconto (nada a receber, nada no caixa)
    if (sale.status === "PAGA" && Number(sale.total ?? 0) === 0 && Number(sale.subtotal ?? 0) > 0) {
      return {
        label: t("salesPage.statuses.courtesy"),
        amount: null,
        className: "bg-secondary text-secondary-foreground border-border",
      };
    }
    return {
      label: sale.status === "PAGA" ? t("salesPage.statuses.paid") : sale.status ?? "—",
      amount: null,
      className:
        "bg-green-100 text-green-900 border-green-200 dark:bg-green-900/30 dark:text-green-100 dark:border-green-900/40",
    };
  };

  const summaryStatusBadge = (status?: "NAO_EMITIDA" | "PROCESSANDO" | "EMITIDA" | "ERRO" | "CANCELADA") => {
    switch (status) {
      case "EMITIDA":
        return {
          label: "Emitida",
          className:
            "bg-green-100 text-green-900 border-green-200 dark:bg-green-900/30 dark:text-green-100 dark:border-green-900/40",
        };
      case "PROCESSANDO":
        return {
          label: "Processando",
          className:
            "bg-amber-100 text-amber-900 border-amber-200 dark:bg-amber-900/30 dark:text-amber-100 dark:border-amber-900/40",
        };
      case "ERRO":
        return {
          label: "Erro",
          className:
            "bg-destructive/10 text-destructive border-destructive/20 dark:bg-destructive/20 dark:text-destructive",
        };
      case "CANCELADA":
        return {
          label: "Cancelada",
          className: "bg-muted text-muted-foreground border-border",
        };
      default:
        return {
          label: "Não emitida",
          className: "bg-muted text-muted-foreground border-border",
        };
    }
  };

  const fiscalStatusByType = (saleId: string, type: "NFCE" | "NFSE") => {
    const summary = fiscalSummaryBySale[saleId];
    if (!summary) return summaryStatusBadge();
    return type === "NFCE"
      ? summaryStatusBadge(summary.nfceStatus ?? summary.status)
      : summaryStatusBadge(summary.nfseStatus ?? summary.status);
  };

  const { data: itemsData } = useItems({ isActive: true });
  const catalogItems = useMemo(
    () =>
      Array.isArray(itemsData)
        ? itemsData
        : (itemsData as { data?: { id: string; name: string; sale_price?: number | null; barcode?: string | null }[] } | undefined)?.data ?? [],
    [itemsData],
  );

  const {
    cart,
    payments,
    suggestedPackageCharges,
    discountType,
    discountValue,
    setDiscountType,
    setDiscountValue,
    saleContext,
    setPetId,
    setOwnerId,
    setBuyerCpf,
    setAppointmentId,
    resumeSaleId,
    setResumeSale,
    setCartFromComandaLines,
    addItemToCart,
    addAdHocItemToCart,
    removeItemFromCart,
    updateItemQuantity,
    updateItemUnitPrice,
    addPayment,
    removePayment,
    getSubtotal,
    getDiscountAmount,
    getTotal,
    getTotalPaid,
    getChange,
    clearCart,
    setSuggestedPackageCharges,
    addSuggestedPackageCharge,
    retainSuggestedPackageCharges,
    updateSuggestedPackageCharge,
    removeSuggestedPackageCharge,
    setCartFromAppointmentItems,
    updateCartItemProfessional,
    updateCartItemPet,
    completeSale,
    isCompleting,
  } = useSales();

  const { data: ownerPetRows = [] } = usePetsByOwner(saleContext.ownerId ?? undefined);
  const ownerPets = useMemo<SalePet[]>(
    () => ownerPetRows.map((pet) => ({ id: pet.id, name: pet.name })),
    [ownerPetRows],
  );
  const petsSummary = useMemo(
    () => summarizeCartPets(cart, saleContext.petId, ownerPets, suggestedPackageCharges),
    [cart, saleContext.petId, ownerPets, suggestedPackageCharges],
  );
  const [adHocItemOpen, setAdHocItemOpen] = useState(false);
  const { data: patientPackages = [], isLoading: isLoadingPatientPackages } = usePatientPackages(saleContext.petId, clinicId);
  const selectedPatientPackageIds = useMemo(
    () => suggestedPackageCharges
      .map((charge) => charge.patientPackageId)
      .filter((value): value is string => Boolean(value)),
    [suggestedPackageCharges],
  );

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);

  const getAppointmentStatusLabel = (appointment: typeof finalizedAppointments[number]) => {
    const statusKey = appointment.status;
    if (!statusKey) return "—";
    return getGroomingStatusLabel(statusKey) || APPOINTMENT_STATUS_LABEL[statusKey] || statusKey;
  };

  const handleCompleteSale = async (options?: { allowReceivable?: boolean }) => {
    const sale = await completeSale(options);
    if (sale) {
      setReceivableDialogOpen(false);
      setLastSaleId(sale.id);
      setShowReceipt(true);
      clearCart();
    }
  };

  const currentTotal = getTotal();
  const currentTotalPaid = getTotalPaid();
  const remainingToReceivable = Math.max(0, currentTotal - currentTotalPaid);
  const isSaleEmpty =
    cart.length === 0 && suggestedPackageCharges.every((charge) => !charge.included || charge.amount <= 0);

  const handleFinalizeClick = async () => {
    // dívida sem responsável não tem de quem cobrar: o "a receber" exige o paciente na venda
    const leavesReceivable = remainingToReceivable > 0 || payments.some((p) => p.method === "A_RECEBER");
    if (leavesReceivable && !saleContext.ownerId) {
      toast({
        title: t("salesPage.receivableNeedsOwnerTitle"),
        description: t("salesPage.receivableNeedsOwnerDescription"),
        variant: "destructive",
      });
      return;
    }
    if (remainingToReceivable > 0) {
      setReceivableDialogOpen(true);
      return;
    }

    await handleCompleteSale();
  };

  const handleNewSale = () => {
    clearCart();
    setShowReceipt(false);
    setPendingAppointment(null);
    setResumeContext(null);
    setResumeSale(null);
  };

  const handleAddExistingPatientPackage = (pkg: PatientPackageSummary) => {
    addSuggestedPackageCharge(buildSuggestedPackageChargeFromPatientPackage(pkg));
  };

  const handleSelectProduct = useCallback(async (product: PdvItem) => {
    if (product.type !== "PACOTE") {
      addItemToCart(product);
      return;
    }

    if (!saleContext.petId) {
      toast({
        title: t("salesPage.selectPatientTitle"),
        description: t("salesPage.selectPatientDescription"),
        variant: "destructive",
      });
      return;
    }

    let catalogPackageId: string | null = null;

    if (customerId) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("catalog_packages")
        .select("id")
        .eq("customer_id", customerId)
        .eq("item_id", product.id)
        .is("deleted_at", null)
        .maybeSingle();

      if (error) {
        toast({
          title: t("salesPage.addPackageErrorTitle"),
          description: t("salesPage.addPackageErrorDescription"),
          variant: "destructive",
        });
        return;
      }

      catalogPackageId = data?.id ?? null;
    }

    addSuggestedPackageCharge(
      buildSuggestedPackageChargeFromCatalogItem(product, {
        catalogPackageId,
        petId: saleContext.petId,
      }),
    );
  }, [addItemToCart, addSuggestedPackageCharge, customerId, saleContext.petId, t, toast]);

  const openSaleDetail = (saleId: string) => {
    setDetailSaleId(saleId);
    setDetailModalOpen(true);
  };

  useEffect(() => {
    const state = (location.state as SalesPrefillLocationState | null) ?? null;
    const saleId = state?.openSaleId;
    if (!saleId) return;

    setDetailSaleId(saleId);
    setDetailModalOpen(true);
    navigate("/sales/history", { replace: true, state: null });
  }, [location.state, navigate]);

  const handleResumeSaleLoaded = useCallback(
    (data: ResumeSaleData) => {
      clearCart();
      setShowReceipt(false);
      setCartFromComandaLines(data.lines);
      setPendingAppointment({
        appointmentId: null,
        petId: data.prefill.petId,
        ownerId: data.prefill.ownerId ?? "",
        petName: data.prefill.petName,
        ownerName: data.prefill.ownerName,
      });
      setResumeSale(data.saleId);
      setResumeContext({ name: data.prefill.petName, kind: data.context, returnTo: data.returnTo });
    },
    [clearCart, setCartFromComandaLines, setResumeSale],
  );

  const handleResumeSaleError = useCallback(
    (reason: ResumeSaleFailure) => {
      const paid = reason === "already_paid";
      toast({
        title: t(paid ? "salesPage.resumeSaleAlreadyPaidTitle" : "salesPage.resumeSaleErrorTitle"),
        description: t(
          paid ? "salesPage.resumeSaleAlreadyPaidDescription" : "salesPage.resumeSaleErrorDescription",
        ),
        variant: "destructive",
      });
    },
    [t, toast],
  );

  const { loading: resumeLoading } = useResumeSaleFromLocation({
    onLoaded: handleResumeSaleLoaded,
    onError: handleResumeSaleError,
  });

  const handleFinalizedAppointmentClick = (appointment: typeof finalizedAppointments[number]) => {
    if (appointment.linked_sale?.id) {
      openSaleDetail(appointment.linked_sale.id);
      navigate("/sales/history");
      return;
    }

    const ownerName = (appointment as { owner?: { name?: string } }).owner?.name ?? '';
    const petName = (appointment as { pet?: { name?: string } }).pet?.name ?? '';
    const petsForPrefill = (appointment.pets?.length
      ? appointment.pets.map((p) => ({ petId: p.id, petName: p.name }))
      : petName
        ? [{ petId: appointment.pet_id, petName }]
        : []) as { petId: string; petName: string }[];

    clearCart();
    setShowReceipt(false);
    setPendingAppointment({
      appointmentId: appointment.id,
      petId: appointment.pet_id,
      ownerId: appointment.owner_id,
      petName,
      ownerName,
      pets: petsForPrefill,
    });
    const items = (appointment as { appointment_items?: Array<{ item_id: string; qty: number; title_snapshot?: string | null; item?: { name?: string; sale_price?: number | null } | null }> }).appointment_items ?? [];
    setCartFromAppointmentItems(items, catalogItems, {
      vet_user_id: appointment.vet_user_id ?? null,
      vet: appointment.vet ?? null,
    });
    setSuggestedPackageCharges(appointment.suggested_package_charges ?? []);
    navigate("/sales/new");
  };

  useEffect(() => {
    const state = (location.state as SalesPrefillLocationState | null) ?? null;
    const prefill = state?.prefillAppointmentForSale;
    if (!prefill) return;

    clearCart();
    setShowReceipt(false);
    setPendingAppointment({
      appointmentId: prefill.appointmentId,
      petId: prefill.petId,
      ownerId: prefill.ownerId,
      petName: prefill.petName,
      ownerName: prefill.ownerName,
      pets: prefill.pets,
    });
    setCartFromAppointmentItems(prefill.appointmentItems ?? [], catalogItems, {
      vet_user_id: prefill.vet_user_id ?? null,
      vet: prefill.vet ?? null,
    });
    setSuggestedPackageCharges(prefill.suggestedPackageCharges ?? []);
    navigate("/sales/new", { replace: true, state: null });
  }, [catalogItems, clearCart, location.state, navigate, setCartFromAppointmentItems, setSuggestedPackageCharges]);

  useEffect(() => {
    if (!saleContext.petId) return;

    retainSuggestedPackageCharges((charge) => {
      if (!charge.petId) return true;
      return charge.petId === saleContext.petId;
    });
  }, [retainSuggestedPackageCharges, saleContext.petId]);

  return (
    <PermissionGuard resource="sales" action="view">
      <div className="space-y-6">
        <div className="flex items-start justify-between">
          <div className="hidden md:block">
            <h1 className="text-3xl font-bold text-foreground">{t("salesPage.title")}</h1>
            <p className="text-muted-foreground mt-2">
              {t("salesPage.description")}
            </p>
          </div>
          {(cart.length > 0 || suggestedPackageCharges.length > 0) && (
            <Button variant="outline" onClick={handleNewSale}>
              <X className="h-4 w-4 mr-2" />
              {t("salesPage.clearSale")}
            </Button>
          )}
        </div>

        <SegmentedTabs
          value={activeTab}
          onValueChange={(value) => navigate(`/sales/${value}`, { replace: true })}
          className="space-y-4"
          fill="screen"
          variant="outline"
          items={[
            {
              value: "new",
              label: <>{t("salesPage.tabs.new")}</>,
              content: (
                <div className="space-y-6">
            {resumeLoading && (
              <div className="rounded-2xl border border-border bg-muted/40 px-4 py-3" aria-live="polite">
                <p className="text-sm font-semibold text-muted-foreground">
                  {t("salesPage.resumeSaleLoading")}
                </p>
                <div className="mt-3 space-y-2" aria-hidden>
                  <div className="h-4 w-2/3 animate-pulse rounded-full bg-muted" />
                  <div className="h-4 w-1/3 animate-pulse rounded-full bg-muted" />
                </div>
              </div>
            )}

            {!resumeLoading && resumeSaleId && resumeContext && (
              <div className="rounded-2xl border border-akuna-orange/35 bg-akuna-orange/10 px-4 py-3">
                <p className="text-sm font-semibold text-foreground">
                  {t(
                    resumeContext.kind === "planCharge"
                      ? "salesPage.resumeSaleBannerPlan"
                      : "salesPage.resumeSaleBanner",
                    { name: resumeContext.name },
                  )}
                </p>
              </div>
            )}

            {showReceipt && resumeContext?.returnTo && (
              <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-state-success/30 bg-state-success-soft px-4 py-3">
                <p className="text-sm font-semibold text-state-success-foreground">
                  {t("salesPage.saleCompleted")}
                </p>
                <Button asChild size="sm" className="ms-auto">
                  <Link to={resumeContext.returnTo}>{t("salesPage.backToOrigin")}</Link>
                </Button>
              </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <Card>
                  <CardContent className="flex items-center pt-6 gap-2">
                    <ProductSearch onSelectProduct={handleSelectProduct} />
                    <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={() => setAdHocItemOpen(true)}>
                      <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                      {t("adHocItem.button")}
                    </Button>
                    <AdHocItemDialog open={adHocItemOpen} onOpenChange={setAdHocItemOpen} onAddItem={addAdHocItemToCart} />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>{t("salesPage.cartTitle", { count: cart.length + suggestedPackageCharges.length })}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CartItems
                      items={cart}
                      packageCharges={suggestedPackageCharges}
                      onUpdateQuantity={updateItemQuantity}
                      onUpdateUnitPrice={updateItemUnitPrice}
                      onRemove={removeItemFromCart}
                      onUpdatePackageCharge={updateSuggestedPackageCharge}
                      onRemovePackageCharge={removeSuggestedPackageCharge}
                      professionals={professionals}
                      onUpdateCartItemProfessional={updateCartItemProfessional}
                      commissionByItem={commissionByItem}
                      pets={ownerPets}
                      salePetId={saleContext.petId}
                      onUpdateCartItemPet={updateCartItemPet}
                    />
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-6">
                <Card>
                  <CardContent className="space-y-6 pt-6">
                    <PatientAndCpfSection
                      saleContext={saleContext}
                      onSetPetId={setPetId}
                      onSetOwnerId={setOwnerId}
                      onSetBuyerCpf={setBuyerCpf}
                      onSetAppointmentId={setAppointmentId}
                      prefillFromAppointment={pendingAppointment}
                      onPrefillApplied={() => setPendingAppointment(null)}
                      variant="embedded"
                      petsSummary={petsSummary}
                    />

                    <Separator />

                    <ExistingPatientPackageSelector
                      packages={patientPackages}
                      selectedPatientPackageIds={selectedPatientPackageIds}
                      isLoading={isLoadingPatientPackages}
                      disabled={!saleContext.petId}
                      onAddPackage={handleAddExistingPatientPackage}
                    />



                    <PaymentSection
                      payments={payments}
                      total={getTotal()}
                      totalPaid={getTotalPaid()}
                      change={getChange()}
                      onAddPayment={addPayment}
                      onRemovePayment={removePayment}
                      discountType={discountType}
                      discountValue={discountValue}
                      onDiscountTypeChange={setDiscountType}
                      onDiscountValueChange={setDiscountValue}
                      subtotal={getSubtotal()}
                      discountAmount={getDiscountAmount()}
                      variant="embedded"
                    />

                    <Separator />

                    <Button
                      onClick={handleFinalizeClick}
                      // cortesia (total zero) finaliza sem pagamento
                      disabled={isSaleEmpty || (payments.length === 0 && currentTotal > 0) || isCompleting}
                      className="w-full"
                      size="lg"
                    >
                      {t("salesPage.finalizeSale")}
                    </Button>

                    <AlertDialog open={receivableDialogOpen} onOpenChange={setReceivableDialogOpen}>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t("salesPage.receivableDialog.title")}</AlertDialogTitle>
                          <AlertDialogDescription>
                            {t("salesPage.receivableDialog.description", { amount: formatCurrency(remainingToReceivable) })}
                          </AlertDialogDescription>
                        </AlertDialogHeader>

                        <div className="space-y-2 rounded-lg border bg-muted/30 p-4 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">{t("salesPage.receivableDialog.total")}</span>
                            <span className="font-medium">{formatCurrency(currentTotal)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">{t("salesPage.receivableDialog.paidNow")}</span>
                            <span className="font-medium">{formatCurrency(currentTotalPaid)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">{t("salesPage.receivableDialog.receivable")}</span>
                            <span className="font-semibold">{formatCurrency(remainingToReceivable)}</span>
                          </div>
                        </div>

                        <AlertDialogFooter>
                          <AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
                          <AlertDialogAction disabled={isCompleting} onClick={() => void handleCompleteSale({ allowReceivable: true })}>
                            {t("salesPage.receivableDialog.saveSale")}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* <Receipt
              open={showReceipt}
              onOpenChange={setShowReceipt}
              saleId={lastSaleId}
              items={cart}
              payments={payments}
              subtotal={getSubtotal()}
              discountAmount={getDiscountAmount()}
              total={getTotal()}
              change={getChange()}
            /> */}
                </div>
              ),
            },
            {
              value: "appointments",
              label: <>{t("salesPage.tabs.appointments")}</>,
              content: (
                <div className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <CalendarCheck className="h-5 w-5" />
                        Agendamentos pendentes de cobrança
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">
                        Clique para iniciar uma nova venda com paciente, CPF, itens do atendimento e cobrancas prontas de pacote.
                      </p>
                    </CardHeader>
                    <CardContent>
                      {isLoadingFinalized ? (
                        <p className="text-muted-foreground">Carregando...</p>
                      ) : finalizedAppointments.length === 0 ? (
                        <p className="text-muted-foreground">Nenhum agendamento pendente de cobrança encontrado.</p>
                      ) : (
                        <div className="overflow-auto max-h-[80vh] rounded-card border border-border">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Data / Horário</TableHead>
                                <TableHead>Tipo</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Paciente</TableHead>
                                <TableHead>Responsável</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {paginatedFinalizedAppointments.map((apt) => {
                                const ownerName = (apt as { owner?: { name?: string } }).owner?.name ?? '—';
                                const patientNames =
                                  (apt.pets?.length
                                    ? apt.pets.map((p) => p.name).join(', ')
                                    : (apt as { pet?: { name?: string } }).pet?.name) ?? '—';
                                const statusKey = apt.status;
                                return (
                                  <TableRow
                                    key={apt.id}
                                    className="cursor-pointer hover:bg-secondary"
                                    onClick={() => handleFinalizedAppointmentClick(apt)}
                                  >
                                    <TableCell>
                                      {apt.starts_at
                                        ? format(new Date(apt.starts_at), "dd/MM/yyyy HH:mm", { locale: ptBR })
                                        : "—"}
                                    </TableCell>
                                    <TableCell>
                                      <AppointmentTypePill
                                        clinicType={(apt as { clinic_appointment_type?: { name: string; color: string; icon: string } | null }).clinic_appointment_type}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <AppointmentStatusBadge
                                        statusKey={statusKey}
                                        statusLabel={getAppointmentStatusLabel(apt)}
                                      />
                                    </TableCell>
                                    <TableCell>{patientNames || '—'}</TableCell>
                                    <TableCell>{ownerName}</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  <div className="flex md:flex-row flex-col gap-2 md:gap-0 items-center justify-between px-4 py-4 border rounded-lg bg-muted/30">
                    <div className="text-sm text-muted-foreground">
                      Mostrando{" "}
                      {finalizedTotalCount === 0
                        ? 0
                        : (finalizedPage - 1) * finalizedPageSize + 1}{" "}
                      a {Math.min(finalizedPage * finalizedPageSize, finalizedTotalCount)} de {finalizedTotalCount}{" "}
                      resultados
                    </div>

                    {finalizedTotalPages > 1 && (
                      <div className="flex md:flex-row flex-col gap-2 md:gap-0 items-center space-x-0 md:space-x-2 w-full md:w-auto">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={goToPreviousFinalizedPage}
                          disabled={finalizedPage === 1}
                          className="w-full md:w-auto"
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Anterior
                        </Button>

                        <div className="flex items-center flex-wrap md:flex-nowrap gap-1 md:gap-0 space-x-0 md:space-x-1">
                          {getPageItems(finalizedPage, finalizedTotalPages).map((item, index) =>
                              item === "ellipsis" ? (
                                <span key={`finalized-ellipsis-${index}`} className="px-2 text-sm text-muted-foreground">
                          ...
                        </span>
                              ) : (
                                <Button
                                  key={item}
                                  variant={finalizedPage === item ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => goToFinalizedPage(item)}
                                  className="w-8 h-8 p-0"
                                >
                                  {item}
                                </Button>
                              ),
                          )}
                        </div>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={goToNextFinalizedPage}
                          disabled={finalizedPage === finalizedTotalPages}
                          className="w-full md:w-auto"
                        >
                          Próximo
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ),
            },
            {
              value: "history",
              label: <>{t("salesPage.tabs.history")}</>,
              content: (
                <div className="space-y-4" tabIndex={-1}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <List className="h-5 w-5" />
                  {t("salesPage.historyTitle")}
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  {t("salesPage.historyDescription")}
                </p>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <SearchField
                      className="w-full md:w-80"
                      label={t("salesPage.historySearchPlaceholder")}
                      clearLabel={t("common:clearSearch")}
                      placeholder={t("salesPage.historySearchPlaceholder")}
                      value={salesSearchTerm}
                      onChange={(e) => setSalesSearchTerm(e.target.value)}
                      onClear={() => setSalesSearchTerm("")}
                      disabled={isLoadingSales}
                    />

                    <Popover open={salesPeriodOpen} onOpenChange={setSalesPeriodOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start gap-2 font-normal md:w-[250px]"
                          disabled={isLoadingSales}
                        >
                          <CalendarIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <span className="truncate">{salesPeriodLabel}</span>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <DateRangeCalendar
                          mode="range"
                          selected={salesPeriodRange}
                          onDayClick={handleSalesPeriodDayClick}
                          numberOfMonths={2}
                          locale={dateLocale}
                        />
                      </PopoverContent>
                    </Popover>

                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="gap-2" disabled={isLoadingSales}>
                          <Filter className="h-4 w-4" />
                          {t("common.status")}
                          {selectedSaleStatuses.length > 0 && (
                            <Badge
                              variant="secondary"
                              className="ml-1 h-5 w-5 rounded-full p-0 flex items-center justify-center"
                            >
                              {selectedSaleStatuses.length}
                            </Badge>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-64 p-4" align="start">
                        <div className="space-y-4">
                          <div className="font-medium text-sm">{t("salesPage.filterByStatus")}</div>
                          <div className="space-y-2">
                            {saleStatusOptions.map((status) => (
                              <div key={status.value} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`sale-status-${status.value}`}
                                  checked={selectedSaleStatuses.includes(status.value)}
                                  onCheckedChange={() => toggleSaleStatus(status.value)}
                                />
                                <Label
                                  htmlFor={`sale-status-${status.value}`}
                                  className="text-sm font-normal cursor-pointer flex-1"
                                >
                                  {status.label}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>

                    {activeSalesFiltersCount > 0 && (
                      <Button variant="ghost" size="sm" onClick={clearSalesFilters} disabled={isLoadingSales}>
                        {t("salesPage.clearFilters", { count: activeSalesFiltersCount })}
                      </Button>
                    )}
                  </div>

                </div>

                {salesListResult?.isTruncated && !isLoadingSales ? (
                  <p className="text-xs text-muted-foreground mb-3">
                    {t("salesPage.limitedResults")}
                  </p>
                ) : null}

                {isLoadingSales ? (
                  <p className="text-muted-foreground">{t("salesPage.pendingLoading")}</p>
                ) : salesList.length === 0 ? (
                  <p className="text-muted-foreground">
                    {activeSalesFiltersCount > 0 ? t("salesPage.emptyFiltered") : t("salesPage.empty")}
                  </p>
                ) : (
                  <div className="overflow-hidden rounded-card border border-border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("salesPage.columns.number")}</TableHead>
                          <TableHead>{t("salesPage.columns.date")}</TableHead>
                          <TableHead className="text-right">{t("salesPage.columns.total")}</TableHead>
                          <TableHead>{t("salesPage.columns.status")}</TableHead>
                          {fiscalEnabled && <TableHead>{t("salesPage.columns.fiscal")}</TableHead>}
                          <TableHead>{t("salesPage.columns.patient")}</TableHead>
                          <TableHead>{t("salesPage.columns.owner")}</TableHead>
                          <TableHead>{t("salesPage.columns.cpf")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {salesList.map((sale) => {
                          const { label, amount, className } = saleStatusLabel(sale);
                          const nfceFiscal = fiscalStatusByType(sale.id, "NFCE");
                          const nfseFiscal = fiscalStatusByType(sale.id, "NFSE");
                          const hasProducts = saleHasProducts(sale);
                          const itemTypes = new Set((sale.sale_items ?? []).map((saleItem) => saleItem.item?.type ?? null));
                          const hasServices = itemTypes.has("SERVICO");
                          const petName = sale.pet?.name ?? null;
                          const ownerName = sale.owner?.name ?? null;
                          const rawCpf = sale.buyer_cpf ?? sale.owner?.document ?? null;
                          const cpfDigits = String(rawCpf ?? "").replace(/\D/g, "");
                          const formattedCpf = cpfDigits.length === 11 ? formatCpf(cpfDigits) : rawCpf;
                          return (
                            <TableRow
                              key={sale.id}
                              className="cursor-pointer hover:bg-secondary"
                              onClick={() => openSaleDetail(sale.id)}
                            >
                              <TableCell className="font-mono">{sale.sale_number}</TableCell>
                              <TableCell>
                                {sale.created_at
                                  ? format(new Date(sale.created_at), "dd/MM/yyyy HH:mm", { locale: ptBR })
                                  : "—"}
                              </TableCell>
                              <TableCell className="text-right font-medium">
                                {formatCurrency(Number(sale.total ?? 0))}
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-wrap items-center gap-2">
                                  <Badge variant="outline" className={className}>
                                    {label}
                                  </Badge>
                                  {amount != null && amount > 0 && (
                                    <span className="text-xs text-muted-foreground">
                                      {formatCurrency(amount)}
                                    </span>
                                  )}
                                </div>
                              </TableCell>
                              {fiscalEnabled && (
                                <TableCell>
                                  <div className="flex flex-wrap gap-1">
                                    {hasProducts && (
                                      <Badge variant="outline" className={nfceFiscal.className}>
                                        NFC-e: {nfceFiscal.label}
                                      </Badge>
                                    )}
                                    {hasServices && (
                                      <Badge variant="outline" className={nfseFiscal.className}>
                                        NFS-e: {nfseFiscal.label}
                                      </Badge>
                                    )}
                                  </div>
                                </TableCell>
                              )}
                              <TableCell className="max-w-[220px] truncate">{petName ?? "—"}</TableCell>
                              <TableCell className="max-w-[260px] truncate">{ownerName ?? "—"}</TableCell>
                              <TableCell className="font-mono">{formattedCpf ?? "—"}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
            {/* Controles de paginação */}
            <div className="flex md:flex-row flex-col gap-2 md:gap-0 items-center justify-between px-4 py-4 border rounded-lg bg-muted/30">
              <div className="text-sm text-muted-foreground">
                {t("salesPage.pagination.showing", {
                  from: salesTotalCount === 0 ? 0 : (salesPage - 1) * SALES_PAGE_SIZE + 1,
                  to: Math.min(salesPage * SALES_PAGE_SIZE, salesTotalCount),
                  total: salesTotalCount,
                })}
              </div>

              {salesTotalPages > 1 && (
                <div className="flex md:flex-row flex-col gap-2 md:gap-0 items-center space-x-0 md:space-x-2 w-full md:w-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goToPreviousPage}
                    disabled={salesPage === 1}
                    className="w-full md:w-auto"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    {t("salesPage.pagination.previous")}
                  </Button>

                  <div className="flex items-center flex-wrap md:flex-nowrap gap-1 md:gap-0 space-x-0 md:space-x-1">
                    {getPageItems(salesPage, salesTotalPages).map((item, index) =>
                      item === "ellipsis" ? (
                        <span key={`ellipsis-${index}`} className="px-2 text-sm text-muted-foreground">
                          ...
                        </span>
                      ) : (
                        <Button
                          key={item}
                          variant={salesPage === item ? "default" : "outline"}
                          size="sm"
                          onClick={() => goToPage(item)}
                          className="w-8 h-8 p-0"
                        >
                          {item}
                        </Button>
                      ),
                    )}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goToNextPage}
                    disabled={salesPage === salesTotalPages}
                    className="w-full md:w-auto"
                  >
                    {t("salesPage.pagination.next")}
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
                </div>
              ),
            },
          ]}
        />

        <SaleDetailModal
          saleId={detailSaleId}
          open={detailModalOpen}
          onOpenChange={(open) => {
            setDetailModalOpen(open);
            if (!open) setDetailSaleId(null);
          }}
        />
      </div>
    </PermissionGuard >
  );
}
